import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';

import {
	AbstractCRUDService,
	AlarisApiService,
	AlarisLanguageService,
	AlarisToasterService,
	ErrorNotifierConfig,
	ExtendableRefBookService,
	sortData,
	WsEntitySubscriptionService,
} from '@campaign-portal/components-library';

import {
	CreateResponse,
	DeleteRequest,
	DeleteResponse,
	ReadResponse,
} from '@campaign-portal/namespace/common/implementations';
import { RPCRequestParams } from '@campaign-portal/namespace/common/rpc.params';
import { exist, Id } from '@campaign-portal/namespace/common/id';
import { Partner } from '@campaign-portal/namespace/entities/partners/specs';
import { PartnerEntity } from '../types';
import { WsResponseAction } from '@campaign-portal/namespace/common/ws';

type PartnerApiResponse = Partner<exist>;

@Injectable({
	providedIn: 'root',
})
export class PartnersService extends ExtendableRefBookService<PartnerEntity<exist>> implements AbstractCRUDService {
	readonly entityType = 'Partners';
	readonly create = this.update;

	constructor(
		private readonly api: AlarisApiService,
		private readonly alarisToaster: AlarisToasterService,
		private readonly langService: AlarisLanguageService,
		private readonly wsEntity: WsEntitySubscriptionService
	) {
		super();
		this.subscribeToWsUpdates();
	}

	get entity(): string {
		return this.langService.translate('notifications.entities.partner');
	}

	get title(): string {
		return this.langService.translate('notifications.titles.partner');
	}

	override load(): Observable<ReadResponse<PartnerEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<PartnerApiResponse[]>>('Partners.Read', {}, this.loading$, this.errorNotifier)
			.pipe(
				map((resp) => {
					const entities = resp.Data.map((p) => this.toEntity(p));
					sortData(entities, 'name');
					return super.process({ ...resp, Data: entities });
				})
			);
	}

	read(params?: RPCRequestParams): Observable<ReadResponse<PartnerEntity<exist>[]>> {
		return this.api
			.loader<ReadResponse<PartnerApiResponse[]>>('Partners.Read', params, this.loading$, this.errorNotifier)
			.pipe(map((resp) => ({ ...resp, Data: resp.Data.map((p) => this.toEntity(p)) })));
	}

	toEntity(partner: PartnerApiResponse): PartnerEntity<exist> {
		return new PartnerEntity({
			...partner,
			currencyId: partner.financialSettings.currencyId,
		});
	}

	update(partner: Partner): Observable<CreateResponse<Partner>> {
		const notify = (response: CreateResponse<Partner>): void => {
			if (response.Success) {
				const message = this.langService.translate(
					partner.id ? 'notifications.actions.update' : 'notifications.actions.create',
					{
						entity: this.entity,
						name: partner.name,
					}
				);
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<CreateResponse<Partner>>(
			`Partners.${partner.id ? 'Update' : 'Create'}`,
			{ Data: { Entities: [partner] } },
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	delete(id: Id<exist>): Observable<DeleteResponse<Partner>> {
		const params: DeleteRequest<Partner<exist>> = { Data: { Ids: [id] } };
		const notify = (response: DeleteResponse<Partner>): void => {
			if (response.Success) {
				const message = this.langService.translate('notifications.actions.delete', { entity: this.entity });
				this.alarisToaster.success(message, this.title);
			}
		};
		return this.api.loader<DeleteResponse<Partner>>(
			'Partners.Delete',
			params,
			this.loading$,
			this.errorNotifier,
			notify
		);
	}

	private readonly errorNotifier = (): ErrorNotifierConfig => ({ title: this.title });

	private subscribeToWsUpdates(): void {
		this.wsEntity.subscribe<Partner<exist>>(this.entityType).subscribe((resp) => {
			switch (resp.Action) {
				case WsResponseAction.CREATE: {
					const entities = (resp.Data?.Entities ?? []).map((partner) => this.toEntity(partner));
					const prepareListFn = (entities: PartnerEntity<exist>[]): PartnerEntity<exist>[] => {
						return sortData([...entities], 'name');
					};
					this.processWsCreate(entities, prepareListFn);
					break;
				}
				case WsResponseAction.UPDATE:
					{
						const entities = (resp.Data?.Entities ?? []).map((partner) => this.toEntity(partner));
						this.processWsUpdate(entities);
					}
					break;
				case WsResponseAction.DELETE:
					this.processWsDelete(resp.Data?.Ids);
					break;
			}
		});
	}
}
